import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type SupportTypeFormProps = {
  supportTypeId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  code: '',
  label: '',
  description: '',
  sort_order: 0,
  active: true,
}

export function SupportTypeForm({ supportTypeId, onSuccess, onCancel }: SupportTypeFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!supportTypeId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)

  const loadOne = useCallback(async () => {
    if (!supportTypeId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/service-catalog/support-types/${supportTypeId}`)
      const r = data?.data ?? data
      setForm({
        code: r.code ?? '',
        label: r.label ?? '',
        description: r.description ?? '',
        sort_order: r.sort_order ?? 0,
        active: r.active === true,
      })
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [supportTypeId, showError])

  useEffect(() => {
    if (supportTypeId) loadOne()
    else setForm(initialValues)
  }, [supportTypeId, loadOne])

  const updateField = (field: keyof typeof form, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        code: form.code.trim(),
        label: form.label.trim() || null,
        description: form.description.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        active: form.active,
      }
      if (supportTypeId) {
        await apiClient.put(`/service-catalog/support-types/${supportTypeId}`, payload)
        showSuccess('Success', 'Support type updated successfully')
      } else {
        await apiClient.post('/service-catalog/support-types', payload)
        showSuccess('Success', 'Support type created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Code *</FormLabel>
          <FormControl isInvalid={!!errors.code?.length} isRequired>
            <Input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="e.g. l1_basic"
              isDisabled={!!supportTypeId}
            />
            {!!supportTypeId && (
              <Box as="span" fontSize="sm" color="gray.500">
                Code cannot be changed after creation.
              </Box>
            )}
            <FormErrorMessage>{errors.code?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Label</FormLabel>
          <FormControl isInvalid={!!errors.label?.length}>
            <Input
              value={form.label}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="e.g. L1 - Basic troubleshooting"
            />
            <FormErrorMessage>{errors.label?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.description?.length}>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Optional notes"
              minH="80px"
            />
            <FormErrorMessage>{errors.description?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Sort order</FormLabel>
          <FormControl isInvalid={!!errors.sort_order?.length}>
            <Input
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(e) => updateField('sort_order', Number(e.target.value))}
            />
            <FormErrorMessage>{errors.sort_order?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              Available in ticket forms and dropdowns
            </Checkbox>
          </FormControl>
        </Grid>
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={supportTypeId ? 'Saving...' : 'Creating...'}>
            {supportTypeId ? 'Save' : 'Create Support Type'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
